import Axios from 'axios';

import { getMoment, getNow } from '@libs/NwMoment';

import {
    getArtDocument,
    getArtDocumentDisplayName,
    getArtDocumentEmbedProvider,
    getArtDocumentPlaybackUrl,
    getArtDocumentPreviewUrl,
    getArtDocumentSource,
    normalizeArtMediaType,
} from '@containers/Art/ArtDocuments';
import { loadArticles } from '@containers/Art/Articles/ArticlesRepository';
import { GALLERY_MEDIA_SOURCE, GALLERY_MEDIA_TYPE, getGalleryVideoEmbedData } from '@containers/Art/Galleries/Galleries.constants';
import { loadGalleries } from '@containers/Art/Galleries/GalleriesRepository';
import { loadNews } from '@containers/Art/News/NewsRepository';
import { UPLOAD_CONTENT_TYPE, UPLOAD_LINKED_FILTER } from '@containers/Art/Uploads/Uploads.constants';
import { loadModelContentById } from '@hooks/API/ModelContents/UseModelContents';

const UPLOADS_SEARCH_API = '/cms/contents/search';
const UPLOADS_CONTENTS_API = '/cms/contents';
const UPLOAD_THUMBNAIL_VARIANT_TYPES = ['Thumbnail', 'Cover', 'LinkedCover'];
const UPLOAD_PLAYBACK_VARIANT_TYPES = ['Standard', 'Video', 'LinkedVideo', 'HD', 'File'];

const normalizeApiResponse = (response) => {
    return response && response.data !== undefined ? response.data : response;
};

const normalizeContentResponse = (response) => {
    const data = normalizeApiResponse(response);
    if (Array.isArray(data)) {
        return data[0] || null;
    }
    return data || null;
};

export const getDefaultUploadsSearchPeriod = () => {
    return [getNow().startOf('month'), getNow().endOf('month')];
};

const findUploadMediaVariant = (upload, variantTypes = []) => {
    const mediaList = upload?.Media || upload?.media;
    if (!upload || !Array.isArray(mediaList)) return null;

    const normalizedVariantTypes = variantTypes.map((variantType) => String(variantType).toLowerCase());
    const mediaFile = mediaList.find((item) =>
        normalizedVariantTypes.includes(String(item.Type || item.type || '').toLowerCase())
    );
    if (!mediaFile?.Url && !mediaFile?.url) return null;

    return {
        FileSize: mediaFile.FileSize,
        Height: mediaFile.Height,
        Url: mediaFile.Url || mediaFile.url,
        Width: mediaFile.Width,
    };
};

export const getUploadMediaFile = (upload, type) => {
    return findUploadMediaVariant(upload, [type]);
};

export const getUploadEmbedProvider = (upload) => {
    const provider = upload?.Provider ?? upload?.provider;
    if (!provider || provider === 'CloudFiles') return undefined;

    const normalizedProvider = String(provider).toLowerCase();
    if (normalizedProvider === 'youtube' || normalizedProvider === 'vimeo') {
        return normalizedProvider;
    }

    return undefined;
};

export const getUploadPlaybackUrl = (upload) => {
    return findUploadMediaVariant(upload, UPLOAD_PLAYBACK_VARIANT_TYPES)?.Url;
};

export const getUploadThumbnailUrl = (upload) => {
    const thumbnailUrl = findUploadMediaVariant(upload, UPLOAD_THUMBNAIL_VARIANT_TYPES)?.Url;
    if (thumbnailUrl) return thumbnailUrl;

    const playbackUrl = getUploadPlaybackUrl(upload);
    if (!playbackUrl) return undefined;

    return getGalleryVideoEmbedData(playbackUrl)?.thumbnailUrl;
};

export const getUploadHdUrl = (upload) => {
    return getUploadMediaFile(upload, 'HD')?.Url;
};

export const isUploadVideo = (upload) => {
    return upload?.Type === UPLOAD_CONTENT_TYPE.WEB_VIDEO;
};

export const isUploadDocument = (upload) => {
    return upload?.Type === UPLOAD_CONTENT_TYPE.WEB_DOCUMENT;
};

export const isUploadLinkedVideo = (upload) => {
    if (!upload) return false;
    if (getUploadEmbedProvider(upload)) return true;
    if (getUploadMediaFile(upload, 'LinkedVideo')?.Url) return true;

    const playbackUrl = getUploadPlaybackUrl(upload);
    return Boolean(playbackUrl && getGalleryVideoEmbedData(playbackUrl));
};

export const isUploadEncodingVideo = (upload) => {
    if (!isUploadVideo(upload)) return false;
    if (isUploadLinkedVideo(upload)) return false;
    return !findUploadMediaVariant(upload, UPLOAD_THUMBNAIL_VARIANT_TYPES)?.Url;
};

const buildUploadMediaFromDocument = (document) => {
    const media = [];
    const previewUrl = getArtDocumentPreviewUrl(document);
    const playbackUrl = getArtDocumentPlaybackUrl(document);
    const isLinkedVideo = getArtDocumentSource(document) === GALLERY_MEDIA_SOURCE.EMBED;

    if (previewUrl) {
        media.push({
            Type: isLinkedVideo ? 'LinkedCover' : 'Thumbnail',
            Url: previewUrl,
        });
    }

    if (playbackUrl) {
        media.push({
            Type: isLinkedVideo ? 'LinkedVideo' : 'Standard',
            Url: playbackUrl,
        });
    }

    return media;
};

/** Maps a fetched Art document to the CMS upload shape used by previews and drawers. */
export const buildUploadFromDocument = (document) => {
    if (!document) return null;

    const documentId = document.ID ?? document.documentID;
    const mediaType = normalizeArtMediaType(document.Type ?? document.type);

    return {
        ArticleIDs: document.ArticleIDs || [],
        GalleryIDs: document.GalleryIDs || [],
        ID: documentId,
        IsLinkedToModelContent: Boolean(document.IsLinkedToModelContent),
        Media: buildUploadMediaFromDocument(document),
        ModelContentIDs: document.ModelContentIDs || [],
        NewsIDs: document.NewsIDs || [],
        Provider: document.Provider,
        Title: getArtDocumentDisplayName(document, 'Media'),
        Type:
            mediaType === GALLERY_MEDIA_TYPE.VIDEO
                ? UPLOAD_CONTENT_TYPE.WEB_VIDEO
                : UPLOAD_CONTENT_TYPE.WEB_PHOTO,
        UploadedAt: document.DateCreated ?? document.UploadedAt,
        UploadedByUserID: document.UserCreated ?? document.UploadedByUserID,
    };
};

export const loadUploadByDocumentId = async (documentId, options) => {
    const document = await getArtDocument(documentId, options);
    return buildUploadFromDocument(document);
};

export const loadUploadContentById = async (contentId) => {
    if (!contentId) return null;

    // This endpoint returns the same upload shape as /cms/contents/search, including linked CMS IDs.
    const response = await Axios.get(`${UPLOADS_CONTENTS_API}/${contentId}`);
    return normalizeContentResponse(response);
};

export const isArtDocumentLinkedVideo = (document) => {
    if (!document) return false;
    if (getArtDocumentSource(document) === GALLERY_MEDIA_SOURCE.EMBED) return true;
    return Boolean(getArtDocumentEmbedProvider(document));
};

export const buildUploadsSearchPayload = (filters = {}) => {
    const types = [];
    if (filters.includePictures) types.push(UPLOAD_CONTENT_TYPE.WEB_PHOTO);
    if (filters.includeVideos) types.push(UPLOAD_CONTENT_TYPE.WEB_VIDEO);
    if (filters.includeFiles) types.push(UPLOAD_CONTENT_TYPE.WEB_DOCUMENT);

    const hasCustomPeriod = Object.prototype.hasOwnProperty.call(filters, 'Period');
    const period = hasCustomPeriod ? filters.Period : getDefaultUploadsSearchPeriod();
    const payload = {
        Types: types,
        UploadedFrom: period ? getMoment(period[0]).startOf('day').toISOString() : null,
        UploadedTo: period ? getMoment(period[1]).endOf('day').toISOString() : null,
    };

    if (filters.uploadedByUserID) {
        payload.UploadedByUserID = filters.uploadedByUserID;
    } else {
        payload.UploadedByUserID = null;
    }

    if (filters.linkedToContent === UPLOAD_LINKED_FILTER.LINKED) {
        payload.IsOrphan = false;
    } else if (filters.linkedToContent === UPLOAD_LINKED_FILTER.ORPHAN) {
        payload.IsOrphan = true;
    }

    return payload;
};

export const searchArtUploads = async (filters = {}) => {
    const response = await Axios.post(UPLOADS_SEARCH_API, buildUploadsSearchPayload(filters));
    const uploads = normalizeApiResponse(response);
    return Array.isArray(uploads) ? uploads : [];
};

export const deleteArtUpload = async (documentId) => {
    await Axios.delete(UPLOADS_CONTENTS_API, {
        data: {
            DocumentIDs: [documentId],
        },
    });
};

export const getLinkedContentAuthorUserId = (item = {}) => {
    return (
        item.AuthorUserID ??
        item.authorUserID ??
        item.UserCreated ??
        item.CreatedByUserID ??
        item.CreatedBy ??
        null
    );
};

export const getLinkedContentCreatedAt = (item = {}) => {
    return item.CreatedAt ?? item.DateCreated ?? item.PublishedDate ?? item.publishedDate ?? null;
};

export const getLinkedContentName = (item = {}, fallbackId) => {
    const name = String(item.Name || item.Title || '').trim();
    return name || `#${fallbackId}`;
};

const getLinkedGalleryId = (item) => {
    if (item === null || item === undefined) return undefined;
    if (typeof item !== 'object') return item;

    return item.GalleryID ?? item.GalleryId ?? item.galleryID ?? item.galleryId ?? item.ID ?? item.id;
};

const getLinkedGalleryItemId = (item) => {
    if (!item || typeof item !== 'object') return undefined;

    return item.ItemID ?? item.ItemId ?? item.itemID ?? item.itemId;
};

const resolveLinkedItems = (ids = [], lookupMap) => {
    return ids
        .map((id) => {
            const item = lookupMap.get(String(id));
            if (!item) return null;

            return {
                ID: item.ID ?? id,
                Name: getLinkedContentName(item, id),
                AuthorUserID: getLinkedContentAuthorUserId(item),
                CreatedAt: getLinkedContentCreatedAt(item),
            };
        })
        .filter(Boolean);
};

const resolveLinkedGalleryItems = (galleryLinks = [], lookupMap) => {
    return galleryLinks
        .map((galleryLink) => {
            const galleryId = getLinkedGalleryId(galleryLink);
            const item = lookupMap.get(String(galleryId));
            if (!item) return null;

            return {
                ID: item.ID ?? galleryId,
                ItemID: getLinkedGalleryItemId(galleryLink),
                Name: getLinkedContentName(item, galleryId),
                AuthorUserID: getLinkedContentAuthorUserId(item),
                CreatedAt: getLinkedContentCreatedAt(item),
            };
        })
        .filter(Boolean);
};

/** Risolve gli ID collegati a un upload in oggetti con nome, autore e data creazione. */
export const loadUploadLinkedContent = async (upload = {}) => {
    const galleryLinks = upload.GalleryIDs || [];
    const galleryIds = galleryLinks.map(getLinkedGalleryId).filter(Boolean);
    const newsIds = upload.NewsIDs || [];
    const articleIds = upload.ArticleIDs || [];
    const modelContentIds = upload.ModelContentIDs || [];

    const [galleries, news, articles, modelContents] = await Promise.all([
        galleryIds.length ? loadGalleries() : Promise.resolve([]),
        newsIds.length ? loadNews() : Promise.resolve([]),
        articleIds.length ? loadArticles() : Promise.resolve([]),
        modelContentIds.length ? Promise.all(modelContentIds.map(loadModelContentById)) : Promise.resolve([]),
    ]);

    const galleryMap = new Map(galleries.map((item) => [String(item.ID), item]));
    const newsMap = new Map(news.map((item) => [String(item.ID), item]));
    const articleMap = new Map(articles.map((item) => [String(item.ID), item]));
    const modelContentMap = new Map(modelContents.filter(Boolean).map((item) => [String(item.ID), item]));

    return {
        galleries: resolveLinkedGalleryItems(galleryLinks, galleryMap),
        news: resolveLinkedItems(newsIds, newsMap),
        articles: resolveLinkedItems(articleIds, articleMap),
        modelContents: resolveLinkedItems(modelContentIds, modelContentMap),
    };
};
